import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  channelScopeKey,
  normaliseChannels,
  offerableChannelRows,
  normaliseCities,
  normaliseCountries,
  normaliseEligiblePackageStatusCodes,
  provincesByCountry,
  withCountryAvailability,
  type CatalogLanguage,
  type ChannelScope,
} from '@mypoz/shared';
import { useLocale } from '@mypoz/i18n';

import {
  OrderCatalogContext,
  type CatalogFetchStatus,
  type OrderCatalogState,
} from './catalogContext.js';
import {
  fetchChannelRows,
  fetchCityRows,
  fetchCountryRows,
  fetchEligiblePackageStatusRows,
  fetchAllProvinceRows,
  fetchOrderChannelRows,
} from './catalog-api.js';

/**
 * A parent-keyed lazy cache: fetch once per key, cache the raw rows, re-normalise them (not
 * re-fetch) when `language` changes. Modelled on `OrdersProvider`'s `loadDetail`/`detailStatus`
 * pair — a `requested` ref set to dedupe in-flight/completed fetches synchronously (a state check
 * inside the state updater itself is not safe: React may run an updater twice), plus a rows map
 * and a status map keyed the same way.
 */
function useLazyCatalog<T>(
  fetchRows: (key: string) => Promise<readonly unknown[]>,
  normalise: (rows: readonly unknown[], language: CatalogLanguage) => readonly T[],
  language: CatalogLanguage,
) {
  const [rows, setRows] = useState<ReadonlyMap<string, readonly unknown[]>>(new Map());
  const [statuses, setStatuses] = useState<ReadonlyMap<string, CatalogFetchStatus>>(new Map());
  const requested = useRef(new Set<string>());

  const load = useCallback(
    (key: string) => {
      if (requested.current.has(key)) return;
      requested.current.add(key);
      setStatuses((current) => new Map(current).set(key, 'loading'));

      void fetchRows(key).then(
        (nextRows) => {
          setRows((current) => new Map(current).set(key, nextRows));
          setStatuses((current) => new Map(current).set(key, 'ready'));
        },
        (cause: unknown) => {
          console.error(`[orders] could not load catalog rows for "${key}"`, cause);
          // Dropped from `requested` so a Retry can ask again.
          requested.current.delete(key);
          setStatuses((current) => new Map(current).set(key, 'error'));
        },
      );
    },
    [fetchRows],
  );

  const resultsFor = useCallback(
    (key: string): readonly T[] => {
      const cached = rows.get(key);
      return cached ? normalise(cached, language) : [];
    },
    [rows, language, normalise],
  );

  const statusFor = useCallback(
    (key: string): CatalogFetchStatus => statuses.get(key) ?? 'idle',
    [statuses],
  );

  return { load, resultsFor, statusFor };
}

/** Channel rows a customer may be offered, normalised — see `offerableChannelRows`. */
function normaliseOfferableChannels(rows: readonly unknown[], language: CatalogLanguage) {
  return normaliseChannels(offerableChannelRows(rows), language);
}

/**
 * Reference data for the create/edit-order flow (MYP-53 slice 2): shipping channels, the
 * destination cascade and the "orderable" package-status codes.
 *
 * Scoped to the two order routes, mounted around `NEW_ORDER_ROUTE`/`EDIT_ORDER_ROUTE` in `App.tsx`
 * — not app-wide, unlike `PackagesProvider`/`OrdersProvider`, which sit above `Layout` because more
 * than one screen shares those lists. This is what the Jira ticket asks for explicitly: "its own
 * provider mounted around the create/edit routes, so 205 channels and 534 cities are not fetched
 * for someone who only opens the Orders list." `order_services` used to be fetched here too, but
 * `OrderDetailPanel` on the Orders list also needs it and has no route of its own to wrap, so it
 * now lives in the separate, app-wide `OrderServicesProvider` instead of pulling this provider — and
 * its 205-row channels list — up to that same app-wide tier.
 *
 * Channels, countries and the eligible-status codes are fetched eagerly together, the same
 * `Promise.all` + StrictMode-safe `current` flag shape `PackagesProvider` and `OrdersProvider` both
 * use. Every country's states come in the same batch (MYP-88): 70 rows in one request, which is
 * also the only way to see that 8 of the 14 countries have none. Cities are different — 534 of
 * them — so each state's are fetched lazily the moment the city select needs them, and cached here
 * by state for the rest of the session.
 */
export function OrderCatalogProvider({ children }: { children: ReactNode }) {
  const { locale } = useLocale();
  const language: CatalogLanguage = locale === 'zh' ? 'zh' : 'en';

  const [channelRows, setChannelRows] = useState<readonly unknown[] | undefined>(undefined);
  const [countryRows, setCountryRows] = useState<readonly unknown[]>([]);
  const [eligibleRows, setEligibleRows] = useState<readonly unknown[]>([]);
  const [provinceRows, setProvinceRows] = useState<readonly unknown[]>([]);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let current = true;

    setChannelRows(undefined);
    setFailed(false);

    Promise.all([
      fetchChannelRows(),
      fetchCountryRows(),
      fetchEligiblePackageStatusRows(),
      fetchAllProvinceRows(),
    ]).then(
      ([nextChannels, nextCountries, nextEligible, nextProvinces]) => {
        if (!current) return;
        setCountryRows(nextCountries);
        setEligibleRows(nextEligible);
        setProvinceRows(nextProvinces);
        setChannelRows(nextChannels);
      },
      (cause: unknown) => {
        if (!current) return;
        console.error('[orders] could not load the order catalog', cause);
        setFailed(true);
      },
    );

    return () => {
      current = false;
    };
  }, [attempt]);

  const reload = useCallback(() => setAttempt((previous) => previous + 1), []);

  const channels = useMemo(
    () => (channelRows ? normaliseChannels(channelRows, language) : []),
    [channelRows, language],
  );
  const provinceMap = useMemo(
    () => provincesByCountry(provinceRows, language),
    [provinceRows, language],
  );
  // Only once the states have arrived can "no states" mean anything — before that, no country is
  // marked unavailable.
  const countries = useMemo(() => {
    const plain = normaliseCountries(countryRows, language);
    return provinceRows.length > 0 ? withCountryAvailability(plain, provinceMap) : plain;
  }, [countryRows, provinceRows.length, provinceMap, language]);
  const catalogStatus: CatalogFetchStatus = channelRows ? 'ready' : failed ? 'error' : 'loading';
  const provincesFor = useCallback(
    (countryCode: string) => provinceMap.get(countryCode) ?? [],
    [provinceMap],
  );
  const provincesStatus = useCallback(
    (_countryCode: string): CatalogFetchStatus => catalogStatus,
    [catalogStatus],
  );
  const eligiblePackageStatusCodes = useMemo(
    () => normaliseEligiblePackageStatusCodes(eligibleRows),
    [eligibleRows],
  );

  /*
    The scoped channel lists (MYP-87), cached per warehouse + goods type. The lazy cache fetches by
    key alone, so the package ids a fetch should send are parked here by `loadChannels` under the
    same key first — the list depends on the key, not the ids (see `channelScopeKey`).
  */
  const scopes = useRef(new Map<string, ChannelScope>());
  const fetchScopedChannels = useCallback((key: string) => {
    const scope = scopes.current.get(key);
    if (scope === undefined) return Promise.reject(new Error(`No scope for "${key}"`));
    return fetchOrderChannelRows(scope);
  }, []);
  const scoped = useLazyCatalog(fetchScopedChannels, normaliseOfferableChannels, language);
  const { load: loadScoped, resultsFor: scopedFor, statusFor: scopedStatus } = scoped;
  const loadChannels = useCallback(
    (scope: ChannelScope) => {
      const key = channelScopeKey(scope);
      scopes.current.set(key, scope);
      loadScoped(key);
    },
    [loadScoped],
  );
  const channelsFor = useCallback(
    (scope: Pick<ChannelScope, 'warehouseId' | 'goodsType'>) => scopedFor(channelScopeKey(scope)),
    [scopedFor],
  );
  const channelsStatus = useCallback(
    (scope: Pick<ChannelScope, 'warehouseId' | 'goodsType'>) =>
      scopedStatus(channelScopeKey(scope)),
    [scopedStatus],
  );

  const cities = useLazyCatalog(fetchCityRows, normaliseCities, language);

  const value = useMemo<OrderCatalogState>(
    () => ({
      channels,
      channelsFor,
      channelsStatus,
      loadChannels,
      countries,
      eligiblePackageStatusCodes,
      status: catalogStatus,
      reload,
      provincesFor,
      provincesStatus,
      citiesFor: cities.resultsFor,
      citiesStatus: cities.statusFor,
      loadCities: cities.load,
    }),
    [
      channels,
      channelsFor,
      channelsStatus,
      loadChannels,
      countries,
      eligiblePackageStatusCodes,
      catalogStatus,
      reload,
      provincesFor,
      provincesStatus,
      cities.resultsFor,
      cities.statusFor,
      cities.load,
    ],
  );

  return <OrderCatalogContext.Provider value={value}>{children}</OrderCatalogContext.Provider>;
}
